import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import { Badge } from '../components/ui';
import { api } from '../lib/api';
import './Dashboard.css';

interface DashboardData {
  asOf: string;
  kpi: {
    totalRevenue: number; totalCollected: number; totalCommission: number;
    activeClients: number; totalClients: number; activeDeals: number;
    totalSales: number; overdueCount: number; overdueBalance: number;
  };
  pipeline: { stage: string; count: number }[];
  recentSales: { date: string; client: string; lot: string; amount: number; stage: string; type: string }[];
  overdueAlerts: { client: string; lot: string; amount: number; dueDate: string }[];
  clientFunnel: { leads: number; prospects: number; active: number; inactive: number };
  inventory: { total: number; available: number; reserved: number; sold: number };
}

const money = (value: number) => new Intl.NumberFormat('en-PH', {
  style: 'currency', currency: 'PHP', minimumFractionDigits: 0, maximumFractionDigits: 2,
}).format(value);
const date = (value: string) => new Date(value).toLocaleDateString('en-PH', {
  year: 'numeric', month: 'short', day: 'numeric',
});
const stageLabel = (value: string) => value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
const stageColors: Record<string, string> = {
  INQUIRY: 'var(--text-muted)', RESERVATION: 'var(--warning)',
  CONTRACT_SIGNING: 'var(--info)', PAYMENT: 'var(--primary-600)', COMPLETED: 'var(--success)',
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(false);
    try { setData(await api.dashboard.stats()); }
    catch { setError(true); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const action = (label: string, path: string) => (
    <button type="button" className="dashboard-link" onClick={() => navigate(path)}>
      {label}<span aria-hidden="true">→</span>
    </button>
  );

  return (
    <>
      <TopBar title="Dashboard" showSearch={false} />
      <div className="dashboard-page">
        <div className="dashboard-intro">
          <div>
            <h2>Operations at a glance</h2>
            <p>Sales, collections, and accounts that need follow-up.</p>
          </div>
          <div className="dashboard-updated">
            {data && <span>Updated {new Date(data.asOf).toLocaleString('en-PH', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
            <button type="button" onClick={fetchStats} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
          </div>
        </div>

        {error && <div className="dashboard-error" role="alert">
          <span>{data ? 'Could not refresh. Showing the last successful update.' : 'Could not load dashboard data. Check the connection and try again.'}</span>
          <button type="button" onClick={fetchStats}>Try again</button>
        </div>}
        {!data && loading && <div className="dashboard-state" role="status">Loading dashboard data…</div>}

        {data && <>
          <div className="dashboard-metrics" aria-label="Key metrics">
            <div className="dashboard-metric">
              <span className="dashboard-metric-label">Sales value</span>
              <strong>{money(data.kpi.totalRevenue)}</strong>
              <span className="dashboard-metric-note">{data.kpi.totalSales} sales · {money(data.kpi.totalCommission)} calculated commission</span>
            </div>
            <div className="dashboard-metric">
              <span className="dashboard-metric-label">Collected</span>
              <strong>{money(data.kpi.totalCollected)}</strong>
              <span className="dashboard-metric-note">{data.kpi.totalRevenue > 0 ? `${Math.round(data.kpi.totalCollected / data.kpi.totalRevenue * 100)}% of sales value` : 'No sales value yet'}</span>
            </div>
            <div className="dashboard-metric">
              <span className="dashboard-metric-label">Open deals</span>
              <strong>{data.kpi.activeDeals}</strong>
              <span className="dashboard-metric-note">Excludes completed and cancelled</span>
            </div>
            <div className={`dashboard-metric ${data.kpi.overdueCount > 0 ? 'dashboard-metric--attention' : ''}`}>
              <span className="dashboard-metric-label">Overdue balance</span>
              <strong>{money(data.kpi.overdueBalance)}</strong>
              <span className="dashboard-metric-note">{data.kpi.overdueCount} unpaid {data.kpi.overdueCount === 1 ? 'payment' : 'payments'} past due</span>
            </div>
          </div>

          <div className="dashboard-main-grid">
            <section className="dashboard-panel" aria-labelledby="dashboard-overdue-title">
              <div className="dashboard-panel-head">
                <div><h3 id="dashboard-overdue-title">Needs follow-up</h3><p>Unpaid payments past their due date</p></div>
                {action('Open payments', '/payments?view=overdue')}
              </div>
              {data.overdueAlerts.length > 0 ? <div className="dashboard-alert-list">
                {data.overdueAlerts.map((item, index) => <div className="dashboard-alert" key={`${item.client}-${item.lot}-${index}`}>
                  <div className="dashboard-alert-details"><strong>{item.client}</strong><span>Lot {item.lot} · Due {date(item.dueDate)}</span></div>
                  <strong className="dashboard-alert-amount">{money(item.amount)}</strong>
                </div>)}
                {data.kpi.overdueCount > data.overdueAlerts.length && <p className="dashboard-panel-foot">Showing {data.overdueAlerts.length} of {data.kpi.overdueCount} past due payments. {action('See all', '/payments?view=overdue')}</p>}
              </div> : <p className="dashboard-empty">No unpaid payments are past due.</p>}
            </section>

            <section className="dashboard-panel" aria-labelledby="dashboard-pipeline-title">
              <div className="dashboard-panel-head">
                <div><h3 id="dashboard-pipeline-title">Sales by stage</h3><p>{data.kpi.totalSales} non-cancelled sales</p></div>
                {action('View pipeline', '/sales')}
              </div>
              {data.kpi.totalSales > 0 ? <div className="dashboard-stage-list">
                {data.pipeline.map(item => <div className="dashboard-stage" key={item.stage}>
                  <span className="dashboard-stage-label">{stageLabel(item.stage)}</span>
                  <div className="dashboard-track" role="img" aria-label={`${item.count} of ${data.kpi.totalSales} sales`}>
                    <span style={{ width: `${item.count / data.kpi.totalSales * 100}%`, background: stageColors[item.stage] || 'var(--text-muted)' }} />
                  </div>
                  <strong>{item.count}</strong>
                </div>)}
                <p className="dashboard-panel-foot">Bars show each stage's share of non-cancelled sales.</p>
              </div> : <p className="dashboard-empty">No sales in the pipeline yet.</p>}
            </section>
          </div>

          <section className="dashboard-section" aria-labelledby="dashboard-sales-title">
            <div className="dashboard-section-head">
              <div><h3 id="dashboard-sales-title">Recent sales</h3><p>Latest records added to the pipeline</p></div>
              {action('View all sales', '/sales')}
            </div>
            {data.recentSales.length > 0 ? <div className="dashboard-table-wrap">
              <table className="dashboard-table">
                <thead><tr><th scope="col">Date added</th><th scope="col">Client</th><th scope="col">Lot</th><th scope="col">Sales value</th><th scope="col">Stage</th><th scope="col">Payment</th></tr></thead>
                <tbody>{data.recentSales.map((sale, index) => <tr key={`${sale.client}-${sale.lot}-${index}`}>
                  <td>{date(sale.date)}</td><td className="dashboard-table-client">{sale.client}</td><td>{sale.lot}</td>
                  <td className="dashboard-table-number">{money(sale.amount)}</td>
                  <td><Badge variant={sale.stage === 'COMPLETED' ? 'available' : sale.stage === 'CANCELLED' ? 'sold' : sale.stage === 'INQUIRY' ? 'default' : 'reserved'}>{stageLabel(sale.stage)}</Badge></td>
                  <td>{stageLabel(sale.type)}</td>
                </tr>)}</tbody>
              </table>
            </div> : <div className="dashboard-empty dashboard-empty--bordered">No sales have been added yet.</div>}
          </section>

          <div className="dashboard-secondary-grid">
            <section className="dashboard-panel" aria-labelledby="dashboard-clients-title">
              <div className="dashboard-panel-head">
                <div><h3 id="dashboard-clients-title">Client status</h3><p>{data.kpi.totalClients} clients in the CRM</p></div>
                {action('View clients', '/clients')}
              </div>
              <div className="dashboard-status-grid">
                {([
                  ['Leads', data.clientFunnel.leads], ['Prospects', data.clientFunnel.prospects],
                  ['Active', data.clientFunnel.active], ['Inactive', data.clientFunnel.inactive],
                ] as [string, number][]).map(([label, count]) => <div className="dashboard-status" key={label}><strong>{count}</strong><span>{label}</span></div>)}
              </div>
            </section>
            <section className="dashboard-panel" aria-labelledby="dashboard-inventory-title">
              <div className="dashboard-panel-head">
                <div><h3 id="dashboard-inventory-title">Lot inventory</h3><p>{data.inventory.total} lots in the map</p></div>
                {action('View map', '/inventory')}
              </div>
              <div className="dashboard-inventory-list">
                {([
                  ['Available', data.inventory.available], ['Reserved', data.inventory.reserved], ['Sold', data.inventory.sold],
                  ...(data.inventory.total > data.inventory.available + data.inventory.reserved + data.inventory.sold
                    ? [['Other statuses', data.inventory.total - data.inventory.available - data.inventory.reserved - data.inventory.sold] as [string, number]] : []),
                ] as [string, number][]).map(([label, count]) => <div className="dashboard-inventory-row" key={label}><span>{label}</span><strong>{count}</strong></div>)}
              </div>
            </section>
          </div>
        </>}
      </div>
    </>
  );
};

export default Dashboard;
